import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLocation } from "react-router-dom";
import UserService from "../Service/UserService";

/**
 * UserManagementPage component to manage and display user information.
 * @returns {JSX.Element} The UserManagementPage component.
 */
function UserManagementPage() {
  const location = useLocation();
  const { user } = location.state || {}; // Extract user from location.state
  const [users, setUsers] = useState([]);
  const [importMessage, setImportMessage] = useState("");
  const [exportMessage, setExportMessage] = useState("");
  const [templateMessage, setTemplateMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const fileInputRef = useRef(null); // Reference to the file input
  const STATUS_MESSAGES = {
    noFile: "Please select a file before uploading.",
    trackMissing: "Track is missing. Unable to export.",
    uploadSuccess: "User Master uploaded successfully.",
    uploadFailed: "Error in uploading file.",
    exportFailed: "Error in exporting file.",
    templateDownloadFailed: "Error in downloading User Master template.",
    noRecentAction: "No recent action.",
    supportedFormat: "Supported format: .xlsx",
  };

  useEffect(() => {
    refreshUsers(user?.track);
  }, []);

  /**
   * Refreshes users from the server by track.
   */
  const refreshUsers = async (track) => {
    if (!track) return;
    try {
      const response = await UserService.getAllUsersByTrack(track);
      //const response = await UserService.getAllUsers();
      setUsers(response);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  /**
   * Deletes a user by ID.
   * @param {string} userId - The ID of the user to delete.
   */
  const deleteUser = async (userId) => {
    try {
      const confirmDelete = window.confirm("Are you sure you want to delete this user?");
      if (confirmDelete) {
        await UserService.deleteUser(userId);
        setUsers(users.filter((user) => user.userId !== userId));
      }
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImportMessage(`File selected: ${file.name}`);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      setImportMessage(STATUS_MESSAGES.noFile);
      return;
    }
    const formData = new FormData();
    formData.append("formData", selectedFile);
    try {
      const response = await UserService.userMasterImport(user.track, formData);
      if (response != null) {
        setImportMessage(STATUS_MESSAGES.uploadSuccess);
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        await refreshUsers(user?.track);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      setImportMessage(STATUS_MESSAGES.uploadFailed + error.message);
    }
  };

  const handleExportUsers = async () => {
    try {
      if (!user?.track) {
        setExportMessage(STATUS_MESSAGES.trackMissing);
        setTemplateMessage("");
        return;
      }
      const response = await UserService.exportUserMasterByTrack(user.track);
      const contentDisposition = response?.headers?.["content-disposition"] || "";
      const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const fileName = fileNameMatch?.[1] || `user-master-${user.track}.xlsx`;

      const fileUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(fileUrl);
      setExportMessage(`${fileName} downloaded successfully.`);
      setTemplateMessage("");
    } catch (error) {
      console.error("Error exporting user master:", error);
      setExportMessage(STATUS_MESSAGES.exportFailed + error.message);
      setTemplateMessage("");
    }
  };

  const handleDownloadUserMasterTemplate = async () => {
    try {
      const response = await UserService.downloadUserMasterTemplate();
      const contentDisposition = response?.headers?.["content-disposition"] || "";
      const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
      const fileName = fileNameMatch?.[1] || "UserMasterTemplate.xlsx";

      const fileUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(fileUrl);

      setTemplateMessage(`${fileName} downloaded successfully.`);
      setExportMessage("");
    } catch (error) {
      console.error("Error downloading user master template:", error);
      setTemplateMessage(STATUS_MESSAGES.templateDownloadFailed + error.message);
      setExportMessage("");
    }
  };

  const userMasterMessage = templateMessage || exportMessage || importMessage;
  const userMasterMessageClass = /failed|error|missing|please/i.test(userMasterMessage)
    ? "text-red-600"
    : "text-blue-600";

  const itemsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(users.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedUsers = users.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [users]);

  return (
    <div>
      <div className="w-11/12 mx-auto">
        <h2 className="text-2xl w-1/3 mx-auto font-semibold mt-2 mb-3">Users Management</h2>

        <div className="w-11/12 max-w-2xl mx-auto bg-gray-100 rounded-lg shadow-lg p-1.5">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <h4 className="inline-flex items-center rounded-full bg-gray-200 px-3 py-1 text-sm font-semibold text-gray-700">
              User Master
            </h4>
            <span className="text-xs text-gray-500">{STATUS_MESSAGES.supportedFormat}</span>
            <input
              type="file"
              accept=".xlsx"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="block w-[230px] sm:w-[260px] text-sm text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              aria-label="Upload user master Excel file"
            />
            <button
              onClick={handleFileUpload}
              className="inline-flex w-auto min-w-[120px] justify-center px-3 py-1.5 text-sm font-semibold text-white bg-blue-500 rounded-lg shadow hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              Upload File
            </button>
          </div>

          <div className="mt-1 flex items-center gap-2 text-sm">
            <p
              className={`min-h-[16px] text-left text-xs font-medium ${userMasterMessageClass}`}
              title={userMasterMessage || STATUS_MESSAGES.noRecentAction}
            >
              {userMasterMessage || STATUS_MESSAGES.noRecentAction}
            </p>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
                onClick={handleExportUsers}
              >
                Download User Master
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900"
                onClick={handleDownloadUserMasterTemplate}
              >
                Download Template
              </button>
              <Link
                to={{
                  pathname: "/admin/add-user",
                  state: { user },
                }}
                className="inline-block bg-green-500 text-white py-1 px-2 rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400"
                aria-label="Add new user"
              >
                Add User
              </Link>
            </div>
          </div>
        </div>
          <br/>
        <div className="h-2" />

        <table className="w-full bg-white border border-gray-300">
          <thead>
            <tr>
               <th className="py-1 px-1 border border-gray-300 text-sm">ID</th>
              <th className="py-1 px-1 border border-gray-300 text-sm">Name</th>
              <th className="py-1 px-1 border border-gray-300 text-sm">Email</th>
              <th className="py-1 px-1 border border-gray-300 text-sm">Designation</th>
              <th className="py-1 px-1 border border-gray-300 text-sm">Track</th>
              <th className="py-1 px-1 border border-gray-300 text-sm">Role</th>
              <th className="py-1 px-1 border border-gray-300 text-sm" colSpan="2">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.map((user) => (
              <tr key={user.userId}>
                <td className="py-1 px-1 border border-gray-300 text-sm">{user.userId}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">{user.name}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">{user.email}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">{user.designation}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">{user.track}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">{user.role}</td>
                <td className="py-1 px-1 border border-gray-300 text-sm">
                  <button
                    className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400"
                    onClick={() => deleteUser(user.userId)}
                  >
                    Delete
                  </button>
                </td>
                <td className="py-2 px-4 border border-gray-300 text-sm">
                  <button className="bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-400">
                    <Link
                      to={{
                        pathname: `/admin/update-user/${user.userId}`,
                        state: { user },
                      }}
                      className="text-purple-700 hover:underline"
                    >
                      Update
                    </Link>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-2 flex items-center justify-center gap-3 text-sm">
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-gray-700">
            Page {currentPage} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>

        <br />
      </div>
    </div>
  );
}

export default UserManagementPage;
